import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { UserPlus, Copy, CheckCircle2, AlertCircle, Mail } from 'lucide-react';

// Same map used in AssignRoleDialog — kept local to avoid coupling.
const DEPARTMENT_ROLES: Record<string, { role: string; label: string }[]> = {
  sales: [
    { role: 'sales_agent', label: 'Sales Agent' },
    { role: 'sales_team_leader', label: 'Sales Team Leader' },
    { role: 'sales_manager', label: 'Sales Manager' },
  ],
  marketing: [
    { role: 'marketing_agent', label: 'Marketing Agent' },
    { role: 'marketing_team_leader', label: 'Marketing Team Leader' },
    { role: 'marketing_manager', label: 'Marketing Manager' },
  ],
  quality_control: [
    { role: 'quality_control_agent', label: 'QC Agent' },
    { role: 'quality_control_lead', label: 'QC Lead' },
  ],
  ad_operations: [
    { role: 'ad_operations_agent', label: 'Ad Ops Agent' },
    { role: 'ad_operations_lead', label: 'Ad Ops Lead' },
  ],
  customer_support: [
    { role: 'customer_support_agent', label: 'Customer Support' },
  ],
  it: [
    { role: 'it_staff', label: 'IT Staff' },
    { role: 'it_manager', label: 'IT Manager' },
  ],
  accounting: [{ role: 'accounting_staff', label: 'Accounting Staff' }],
  hr: [{ role: 'hr_staff', label: 'HR Staff' }],
};

interface InviteStaffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface InviteResult {
  user_id: string;
  already_existed: boolean;
  temp_password: string | null;
  reset_link: string | null;
}

export function InviteStaffDialog({ open, onOpenChange }: InviteStaffDialogProps) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [deptId, setDeptId] = useState('');
  const [role, setRole] = useState('');
  const [teamId, setTeamId] = useState('');
  const [result, setResult] = useState<InviteResult | null>(null);

  const { data: departments = [] } = useQuery({
    queryKey: ['internal-departments-invite'],
    queryFn: async () => {
      const { data, error } = await supabase.from('internal_departments').select('*').order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const selectedDept = useMemo(
    () => departments.find((d: any) => d.id === deptId),
    [departments, deptId],
  );

  const { data: teams = [] } = useQuery({
    queryKey: ['internal-teams-invite', deptId],
    queryFn: async () => {
      if (!deptId) return [];
      const { data, error } = await supabase
        .from('internal_teams')
        .select('id, name')
        .eq('department_id', deptId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!deptId,
  });

  const availableRoles = useMemo(() => {
    if (!selectedDept) return [];
    return DEPARTMENT_ROLES[selectedDept.slug] || [];
  }, [selectedDept]);

  const inviteMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('invite-staff', {
        body: {
          email: email.trim().toLowerCase(),
          full_name: fullName.trim(),
          phone: phone.trim() || null,
          role,
          department_id: deptId,
          team_id: teamId && teamId !== 'none' ? teamId : null,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return data as InviteResult;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['internal-teams-all'] });
      setResult(data);
      if (data.already_existed) {
        toast.success('Existing account — role and team assigned.');
      } else {
        toast.success('Staff member invited.');
      }
    },
    onError: (err: any) => toast.error(err.message || 'Failed to invite'),
  });

  const reset = () => {
    setEmail('');
    setFullName('');
    setPhone('');
    setDeptId('');
    setRole('');
    setTeamId('');
    setResult(null);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const isValid = email.trim() && fullName.trim() && deptId && role;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" /> Invite Staff Member
          </DialogTitle>
          <DialogDescription>
            Create a new internal team account with a role and team assignment.
          </DialogDescription>
        </DialogHeader>

        {/* Result view — shown after successful invite */}
        {result ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
              <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
              <div className="text-sm">
                {result.already_existed ? (
                  <p>This email already had an account. Role and team have been added.</p>
                ) : (
                  <p>Account created successfully. Share the credentials below.</p>
                )}
              </div>
            </div>

            {result.temp_password && (
              <div className="space-y-2">
                <Label className="text-xs">Temporary Password</Label>
                <div className="flex gap-2">
                  <Input value={result.temp_password} readOnly className="font-mono text-sm" />
                  <Button size="sm" variant="outline" onClick={() => copy(result.temp_password!)}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground flex items-start gap-1">
                  <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                  Share securely. The user should change it on first login.
                </p>
              </div>
            )}

            {result.reset_link && (
              <div className="space-y-2">
                <Label className="text-xs">Password Setup Link (recommended)</Label>
                <div className="flex gap-2">
                  <Input value={result.reset_link} readOnly className="text-xs" />
                  <Button size="sm" variant="outline" onClick={() => copy(result.reset_link!)}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground flex items-start gap-1">
                  <Mail className="h-3 w-3 mt-0.5 shrink-0" />
                  Send this link to the new staff member so they can set their own password.
                </p>
              </div>
            )}

            <DialogFooter className="gap-2 pt-2">
              <Button variant="outline" onClick={() => { reset(); }}>Invite Another</Button>
              <Button onClick={close}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Identity */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="invite-name" className="text-xs">Full Name *</Label>
                <Input
                  id="invite-name"
                  placeholder="e.g. Eyob Bekele"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-email" className="text-xs">Email *</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="name@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="invite-phone" className="text-xs">Phone (optional)</Label>
                <Input
                  id="invite-phone"
                  placeholder="+251 …"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>

            {/* Assignment */}
            <div className="space-y-3">
              <Label className="text-xs font-medium text-muted-foreground">Role Assignment</Label>
              <Select value={deptId} onValueChange={(v) => { setDeptId(v); setRole(''); setTeamId(''); }}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Select Department *" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d: any) => (
                    <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedDept && availableRoles.length > 0 && (
                <Select value={role} onValueChange={setRole}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select Role *" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRoles.map((r) => (
                      <SelectItem key={r.role} value={r.role}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {role && (
                <Select value={teamId} onValueChange={setTeamId}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Assign to Team (recommended)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Team</SelectItem>
                    {teams.map((t: any) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {role && teamId === 'none' && (
                <div className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 p-2 rounded-md">
                  <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <p>Without a team, this user won't inherit team-level governance scope (e.g. <code>approve_listings</code>). They'll only get the base permissions of the {availableRoles.find(r => r.role === role)?.label} role.</p>
                </div>
              )}
            </div>

            {role && selectedDept && (
              <div className="p-3 rounded-lg bg-muted/50 border text-xs space-y-1">
                <div className="font-medium">Preview</div>
                <p className="text-muted-foreground">
                  <strong>{fullName || 'New staff'}</strong> will be created with role
                  <Badge variant="secondary" className="text-[10px] mx-1">
                    {availableRoles.find(r => r.role === role)?.label}
                  </Badge>
                  in <strong>{selectedDept.name}</strong>
                  {teamId && teamId !== 'none' && (
                    <> on team <strong>{teams.find((t: any) => t.id === teamId)?.name}</strong></>
                  )}.
                  A temporary password and password-setup link will be returned.
                </p>
              </div>
            )}

            <DialogFooter className="gap-2 pt-2">
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button
                onClick={() => inviteMutation.mutate()}
                disabled={!isValid || inviteMutation.isPending}
              >
                {inviteMutation.isPending ? 'Inviting…' : 'Invite Staff'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
